/**
 * Notification Bell Component
 * Displays quote notifications with unread count badge
 */

import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { quoteNotificationService, type Notification } from '@/services/QuoteNotificationService';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Subscribe to notification updates
    const unsubscribe = quoteNotificationService.subscribe((updatedNotifications) => {
      setNotifications(updatedNotifications);
    });

    // Get initial notifications
    setNotifications(quoteNotificationService.getNotifications());

    return unsubscribe;
  }, []);

  const unreadCount = quoteNotificationService.getUnreadCount();

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    quoteNotificationService.markAsRead(notification.id);

    // Navigate to quote if quote_id exists
    if (notification.quote_id) {
      navigate(`/quotes/${notification.quote_id}`);
      setOpen(false);
    }
  };

  const handleMarkAllRead = () => {
    quoteNotificationService.markAllAsRead();
  };

  const handleClearAll = () => {
    quoteNotificationService.clearAll();
  };

  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'bg-destructive/10 border-destructive/30 hover:bg-destructive/10';
      case 'high':
        return 'bg-warning-muted border-warning/30 hover:bg-warning-muted';
      case 'medium':
        return 'bg-accent border-primary/30 hover:bg-accent';
      case 'low':
        return 'bg-muted border-border hover:bg-muted';
      default:
        return 'bg-muted border-border hover:bg-muted';
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative hover:bg-accent"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                className="text-xs h-7"
              >
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="text-xs h-7"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
              <Bell className="h-12 w-12 mb-2 opacity-20" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 cursor-pointer transition-colors border-l-4 ${
                    !notification.read ? getPriorityColor(notification.priority) : 'hover:bg-muted'
                  } ${!notification.read ? 'border-l-primary' : 'border-l-transparent'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium truncate">
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-accent0 rounded-full flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge
                          variant="outline"
                          className="text-xs"
                        >
                          {notification.type.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => {
                  navigate('/quotes');
                  setOpen(false);
                }}
              >
                View all quotes
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
